/**
 * SimulationStrategy - シミュレーション評価ストラテジー
 *
 * hardAIをベースに、魔法カード選択をシミュレーション評価で改善。
 * 魔法を「仮に使う」→ 結果の盤面をスコア化 → 最高スコアのカードを選択
 */

import { hardStrategy } from './hard';
import type { AIStrategy, AIGameState } from './base';
import type { CardWithId } from '../../../types/card';
import type { GameState, PlayerState } from '../../gameEngine/GameState';
import { GAME_STATUS, PHASES, createTurnFlags } from '../../gameEngine/GameState';
import { createDeepSeaZone } from '../../deepSeaZone';
import {
  chooseBestMagicCard,
  DEFAULT_WEIGHTS,
  DEFAULT_MAGIC_CONFIG,
  type EvaluationWeights,
  type MagicEvaluationConfig,
} from '../evaluation';
import type { AIGenome } from '../genetic/genome';

// =============================================================================
// AIGameState → GameState 変換
// =============================================================================

/**
 * AIGameStateをGameStateに変換
 *
 * シミュレーション評価に必要な形式に変換。
 * 一部の情報は近似値を使用。
 */
export function convertToGameState(aiState: AIGameState): GameState {
  const p1State: PlayerState = {
    life: aiState.currentPlayer === 1 ? aiState.myLife : aiState.opponentLife,
    deck: aiState.currentPlayer === 1 ? [...aiState.myDeck] : [...aiState.opponentDeck],
    hand: aiState.currentPlayer === 1 ? [...aiState.myHand] : [],
    field: aiState.currentPlayer === 1
      ? [...aiState.myField]
      : [...aiState.opponentField],
    graveyard: aiState.currentPlayer === 1 ? [...aiState.myGraveyard] : [...aiState.opponentGraveyard],
    activeSP: aiState.currentPlayer === 1 ? aiState.myActiveSP : 0,
    restedSP: aiState.currentPlayer === 1 ? aiState.myRestedSP : 0,
    fieldCard: aiState.currentPlayer === 1 ? aiState.myFieldCard : aiState.opponentFieldCard,
    phaseCard: aiState.currentPlayer === 1 ? aiState.myPhaseCard : aiState.opponentPhaseCard,
    statusEffects: aiState.currentPlayer === 1 ? [...aiState.myStatusEffects] : [],
    nextTurnSPBonus: 0,
    magicBlocked: false,
    spReduction: 0,
    bonusSP: aiState.currentPlayer === 1 ? (aiState.myBonusSP || 0) : 0,
    deepSeaZone: aiState.currentPlayer === 1
      ? (aiState.myDeepSeaZone || createDeepSeaZone())
      : (aiState.opponentDeepSeaZone || createDeepSeaZone()),
  };

  const p2State: PlayerState = {
    life: aiState.currentPlayer === 2 ? aiState.myLife : aiState.opponentLife,
    deck: aiState.currentPlayer === 2 ? [...aiState.myDeck] : [...aiState.opponentDeck],
    hand: aiState.currentPlayer === 2 ? [...aiState.myHand] : [],
    field: aiState.currentPlayer === 2
      ? [...aiState.myField]
      : [...aiState.opponentField],
    graveyard: aiState.currentPlayer === 2 ? [...aiState.myGraveyard] : [...aiState.opponentGraveyard],
    activeSP: aiState.currentPlayer === 2 ? aiState.myActiveSP : 0,
    restedSP: aiState.currentPlayer === 2 ? aiState.myRestedSP : 0,
    fieldCard: aiState.currentPlayer === 2 ? aiState.myFieldCard : aiState.opponentFieldCard,
    phaseCard: aiState.currentPlayer === 2 ? aiState.myPhaseCard : aiState.opponentPhaseCard,
    statusEffects: aiState.currentPlayer === 2 ? [...aiState.myStatusEffects] : [],
    nextTurnSPBonus: 0,
    magicBlocked: false,
    spReduction: 0,
    bonusSP: aiState.currentPlayer === 2 ? (aiState.myBonusSP || 0) : 0,
    deepSeaZone: aiState.currentPlayer === 2
      ? (aiState.myDeepSeaZone || createDeepSeaZone())
      : (aiState.opponentDeepSeaZone || createDeepSeaZone()),
  };

  return {
    config: {
      summoningSickness: true,
      secondPlayerBonusSP: true,
      piercingDamage: false,
      loneWarriorRule: true,
      initialLifePoints: 6000,
    },
    firstPlayer: aiState.firstPlayer || 1,
    turn: aiState.turn,
    currentPlayer: aiState.currentPlayer,
    phase: aiState.phase as typeof PHASES[keyof typeof PHASES],
    isFirstTurn: aiState.isFirstTurn,
    winner: null,
    gameStatus: GAME_STATUS.PLAYING,
    p1: p1State,
    p2: p2State,
    turnFlags: createTurnFlags(),
    triggers: {},
    logs: [],
    playedCardsHistory: { p1: [], p2: [] },
  };
}

// =============================================================================
// シミュレーションストラテジー
// =============================================================================

/**
 * シミュレーション評価ストラテジーを生成
 *
 * @param weights - 盤面評価パラメータ（省略時はデフォルト）
 * @param config - 魔法評価設定（省略時はデフォルト）
 * @returns シミュレーション評価ストラテジー
 */
export function createSimulationStrategy(
  weights?: EvaluationWeights,
  config?: MagicEvaluationConfig
): AIStrategy {
  const w = weights || DEFAULT_WEIGHTS;
  const c = config || DEFAULT_MAGIC_CONFIG;

  return {
    ...hardStrategy,

    /**
     * 魔法カード選択（シミュレーション評価）
     *
     * 各魔法カードの使用をシミュレートし、
     * 盤面スコアの改善が最も大きいカードを選択。
     */
    chooseMagicCard(usableMagicCards: CardWithId[], gameState: AIGameState): CardWithId | null {
      if (usableMagicCards.length === 0) return null;

      try {
        // AIGameStateをGameStateに変換
        const state = convertToGameState(gameState);

        // シミュレーション評価で最適カードを選択
        const bestCard = chooseBestMagicCard(
          usableMagicCards,
          state,
          gameState.currentPlayer,
          w,
          c
        );

        // シミュレーションで良いカードが見つからなかった場合はhardStrategyにフォールバック
        if (!bestCard) {
          return hardStrategy.chooseMagicCard(usableMagicCards, gameState);
        }

        return bestCard;
      } catch (error) {
        // エラー時はhardStrategyにフォールバック
        console.warn('SimulationStrategy error, falling back to hardStrategy:', error);
        return hardStrategy.chooseMagicCard(usableMagicCards, gameState);
      }
    },
  };
}

/**
 * デフォルトのシミュレーションストラテジー
 *
 * hardStrategyをベースに、魔法カード選択をシミュレーション評価で改善。
 */
export const simulationStrategy: AIStrategy = createSimulationStrategy();

/**
 * カスタムパラメータでシミュレーションストラテジーを生成（GA統合用）
 *
 * @param weights - 盤面評価パラメータ
 * @param config - 魔法評価設定
 * @returns カスタマイズされたストラテジー
 */
export function createCustomSimulationStrategy(
  weights: EvaluationWeights,
  config: MagicEvaluationConfig
): AIStrategy {
  return createSimulationStrategy(weights, config);
}

// =============================================================================
// GA統合
// =============================================================================

/**
 * AIGenomeからシミュレーションストラテジーを生成
 *
 * GAで最適化されたゲノムのboardEvaluationとmagicEvaluationパラメータを使用。
 *
 * @param genome - GAで最適化されたAIGenome
 * @returns シミュレーション評価ストラテジー
 */
export function createGeneticSimulationStrategy(genome: AIGenome): AIStrategy {
  return createSimulationStrategy(
    genome.boardEvaluation,
    genome.magicEvaluation
  );
}
